import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { getBackendUrl } from "@src/utils/getBackendUrl";

export interface MarketplaceEvent {
	marketplace: string;
	marketplaceEventId: string;
	name: string;
	eventDateTime: string;
	localEventDateTime: string | null;
	marketplaceVenueId: string | null;
	marketplaceHeadlinerIds: Array<string>;
	metadata: Record<string, unknown>;
	id: string;
	isActive: boolean;
	isAutomaticallyMapped: boolean;
	isProcessed: boolean;
	isDeleted: boolean;
	isValidated: boolean;
	sleptDate: string | null;
	sleptBy?: string;
	awakeDate: string | null;
	mappedStageFrontEventId: string | null;
	notes: Array<unknown>;
	audit?: Record<string, unknown>;
	automapperResult?: unknown;
}

export interface SearchMarketplaceEventsPayload {
	marketplace: string;
	eventName: string;
	dateFrom: string;
	dateTo: string;
	eventID: string | null;
}

interface PullEventsState {
	results: Array<MarketplaceEvent>;
	loading: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
	saveStatus: "idle" | "pending" | "succeeded" | "failed";
	saveError: string | null;
	dbCount: number;
}

const PULL_EVENTS_URL = `${getBackendUrl()}/search-marketplace-events`;
const POST_EVENTS_URL = `${getBackendUrl()}/post-marketplace-events`;

const initialState: PullEventsState = {
	results: [],
	loading: "idle",
	error: null,
	saveStatus: "idle",
	saveError: null,
	dbCount: 0,
};

interface SearchMarketplaceEventsResponse {
	data: Array<MarketplaceEvent>;
	dbCount: number;
}

export const searchMarketplaceEvents = createAsyncThunk<
	SearchMarketplaceEventsResponse,
	SearchMarketplaceEventsPayload,
	{ rejectValue: string }
>("pullEvents/searchMarketplaceEvents", async (payload, { rejectWithValue }) => {
	try {
		const response = await axios.post(PULL_EVENTS_URL, payload, {
			headers: getAuthHeaders(),
		});

		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const message =
				(error.response?.data as { message?: string } | undefined)?.message ||
				error.message ||
				"Failed to search marketplace events";
			return rejectWithValue(message);
		}

		return rejectWithValue("Failed to search marketplace events");
	}
});

interface SaveMarketplaceEventsPayload {
	marketplace: string;
	events: Array<MarketplaceEvent>;
}

export const saveMarketplaceEvents = createAsyncThunk<
	Array<MarketplaceEvent>,
	SaveMarketplaceEventsPayload,
	{ rejectValue: string }
>("pullEvents/saveMarketplaceEvents", async ({ marketplace, events }, { rejectWithValue }) => {
	try {
		const metadataOnlyPayload = events.map((event) => event.metadata);
		const response = await axios.post(
			POST_EVENTS_URL,
			{
				marketplace,
				events: metadataOnlyPayload,
			},
			{ headers: getAuthHeaders() },
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const message =
				(error.response?.data as { message?: string } | undefined)?.message ||
				error.message ||
				"Failed to save marketplace events";
			return rejectWithValue(message);
		}

		return rejectWithValue("Failed to save marketplace events");
	}
});

export const pullEventsSlice = createSlice({
	name: "pullEvents",
	initialState,
	reducers: {
		resetResults: (state) => {
			state.results = [];
			state.loading = "idle";
			state.error = null;
			state.saveStatus = "idle";
			state.saveError = null;
			state.dbCount = 0;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(searchMarketplaceEvents.pending, (state) => {
				state.loading = "pending";
				state.error = null;
				state.saveStatus = "idle";
				state.saveError = null;
			})
			.addCase(searchMarketplaceEvents.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.results = action.payload.data;
				state.dbCount = action.payload.dbCount;
			})
			.addCase(searchMarketplaceEvents.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload || "Failed to search marketplace events";
				state.results = [];
			})
			.addCase(saveMarketplaceEvents.pending, (state) => {
				state.saveStatus = "pending";
				state.saveError = null;
			})
			.addCase(saveMarketplaceEvents.fulfilled, (state) => {
				state.saveStatus = "succeeded";
			})
			.addCase(saveMarketplaceEvents.rejected, (state, action) => {
				state.saveStatus = "failed";
				state.saveError = action.payload || "Failed to save marketplace events";
			});
	},
});

export const { resetResults } = pullEventsSlice.actions;
